import _ from 'lodash';
import config from 'app-core/config';
import Data from 'app-core/Data';
import auth from 'auth';

let isProcessingDefaultMovement = false;
let isProcessingHierarchicalChanges = false;
let changedTracks = {};
let tracker = null;

const getTrackInfo = (t) => {
	let start = t.start;
	let end = t.start + t.getTrimmedDuration();

	if (changedTracks[t.id]) {
		start -= changedTracks[t.id]?.start;
		end -= changedTracks[t.id]?.end;
	}

	return {
		start,
		end
	}
}

const processLinkedDelta = (processedTrack, sourceLinkType, delta, results) => {
	const info = {
		start: 0,
		end: 0,
		track: processedTrack,
		changes: {}
	};

	if (!results[processedTrack.id]) {
		results[processedTrack.id] = {
			start: 0,
			startOffset: 0,
			endOffset: 0,
			name: processedTrack.name(),
			getTrack: () => processedTrack
		}
	}

	if ((sourceLinkType || 'both') === 'both') {
		if (processedTrack.__linkedTo.some(f => f.sourceLinkType === 'start')) {
			sourceLinkType = 'end';
		} else if (processedTrack.__linkedTo.some(f => f.sourceLinkType === 'end')) {
			sourceLinkType = 'start';
		} else {
			info.start += delta;
			info.end += delta;
			info.changes.start = info.start;
			info.changes.end = info.end;
			results[processedTrack.id].start += delta;
		}
	}
	if (sourceLinkType === 'start') {
		let startOffset = processedTrack.startOffset;
		info.start = startOffset - processedTrack.startOffset;
		info.changes.startOffset = info.start;
		results[processedTrack.id].start += delta;
		results[processedTrack.id].endOffset += delta;
	}
	if (sourceLinkType === 'end') {
		let endOffset = processedTrack.endOffset;
		info.end = endOffset - processedTrack.endOffset;
		info.changes.endOffset = info.end;
		results[processedTrack.id].endOffset -= delta;
	}
	
	changedTracks[processedTrack.id] = info;
}

const processDefaultSSMLMoving = (track, changes, results) => {
	if (isProcessingDefaultMovement || track.type !== 'textToSpeech') return;
	isProcessingDefaultMovement = track.id;
	
	try {
		const original = getTrackInfo(track);
		const processingTracks = track.__tracker.tracks.filter(f => 
			f.id !== track.id
			&& f.trackNumber() === track.trackNumber()
			&& changes.end && f.start >= original.start
			&& f.__linkedTo?.length === 0
			&& !f.__isSubTrack
			&& !changedTracks[f.id]
		);

		return processingTracks.sort((a,b) => a.start - b.start).map(procTrack => {
			let processingType = procTrack.start < original.start ? 'end' : 'both';
			if (processingType === 'end') {
				if (procTrack.endOffset > 0) {
					processLinkedDelta(procTrack, processingType, changes.end, results);
				}
			} else {
				processLinkedDelta(procTrack, processingType, changes.end, results);
			}

			return {
				track: procTrack,
				changed: 'end',
				processingType
			}
		});
	} finally {
		isProcessingDefaultMovement = null;
	}
}

const processHierarchicalChanges = () => {
	if (isProcessingHierarchicalChanges) return;

	isProcessingHierarchicalChanges = true;

	let hierarchicalResults = {};

	try {
		const processLinks = (t, changes) => {
			t.__linkedFrom.map(f => {
				if ((changes.start && f.linkType === 'start') || (changes.end && (f.linkType || 'end') === 'end')) {
					processLinkedDelta(f.track, f.sourceLinkType, f.linkType === 'start' ? changes.start : changes.end, hierarchicalResults);
					processLinks(f.track, changes);
				}
			});
		}

		Object.keys(changedTracks).map(id => {
			const track = changedTracks[id].track;
			const changes = {
				start: changedTracks[id].start,
				end: changedTracks[id].end
			}

			//Do not process default SSML while moving clip itself
			if (!changedTracks[id]?.changes?.start) {
				const affectedTracks = processDefaultSSMLMoving(track, changes, hierarchicalResults);
				if (__DEBUG__)
					console.debug('Links def'/*, { affectedTracks, changes }*/);
				(affectedTracks || []).map(f => {
					processLinks(f.track, changedTracks[f.track.id] || changes);
				});
			}

			processLinks(track, changes);
		});
		
		Object.keys(hierarchicalResults).map(key => {
			let f = hierarchicalResults[key];
			let track = f?.getTrack();
			if (track) {
				track.start += f.start || 0;
				track.endOffset = Math.max(0, Math.min(track.endOffset + (f.endOffset || 0), track.getTrimmedDuration(true) - 0.1));
				track.startOffset = Math.max(0, Math.min(track.startOffset + (f.startOffset || 0), track.getTrimmedDuration(true) - 0.1));
				track.__saveInsertion('links', void 0, true);

				if (__DEBUG__)
					console.debug(`processLinkedTracks -> ${Data.secondsToDisplayValue(track.start)} ${track.name()}: [${f.startOffset}] (${track.getTrimmedDuration()}) [${f.endOffset}]`);
			}
		});
		
	} finally {
		isProcessingHierarchicalChanges = false;
	}
}

const timeProcessing = 200;
let resolves = [];
const processImmediate = () => {
	const round = (v) => parseInt(v * 1000) / 1000 || 0;

	Object.keys(changedTracks).map(t => {
		if (!changedTracks[t].changes) {
			changedTracks[t].changes = {};
		}
		const changesList = changedTracks[t].changes;
		changedTracks[t].start = round(changesList.start || 0);
		changedTracks[t].end = round(changesList.end) || round(changesList.endOffset * -1) + (round(changesList.start) - round(changesList.startOffset))
	});

	processHierarchicalChanges();

	if (__DEBUG__)
		console.debug('Links'/*, { changedTracks }*/);
	
	changedTracks = {};

	resolves.map(f => f(true));
	resolves = [];
}

const process = _.debounce(processImmediate, timeProcessing, { 
	leading: false,
	trailing: true,
	maxWait: timeProcessing * 2,
});

export default {
	defineLinkUnlinkProperties(track) {
		tracker = track.__tracker;

		_.merge(track.insertion.JsonConfig, {
			linkedTo: track.insertion.JsonConfig?.linkedTo || [],
			linkedFrom: track.insertion.JsonConfig?.linkedFrom || []
		});

		const hasLinksBetween = (dest) => {
			const allLinkedTracks = [];
			const check = (t, source) => {
				[...t.insertion.JsonConfig.linkedTo.filter(f => f?.id !== source?.id).map(f => f?.id)].map(f => {
					const linkedTrack = track.__tracker.tracks.find(t => t.id === f);
					if (linkedTrack && !allLinkedTracks.includes(f)) {
						allLinkedTracks.push(f);
						check(linkedTrack, t);
					}
				})
			}
			check(dest, track);
			if (__DEBUG__)
				console.debug('Has links between'/*, { allLinkedTracks, includes: allLinkedTracks.includes(track.id) }*/);
			return allLinkedTracks.includes(track.id);
		}

		const link = (destTrack, opts) => {
			if (hasLinksBetween(destTrack)) {
				config.getVueApp().$notifyWarning(config.getVueApp().$tCaption('Detected recursive link. Operation blocked.'));
				return;
			}
			if (destTrack && !track.insertion.JsonConfig.linkedTo.some(f => f?.id === destTrack.id)) {
				track.insertion.JsonConfig.linkedTo.push({
					linkType: 'end',
					sourceLinkType: 'both',
					...(opts || {}),
					id: destTrack.id,
				});
				track.__emitDataChanged();
			}

			if (destTrack && !destTrack.insertion.JsonConfig.linkedFrom.some(f => f?.id === track.id)) {
				destTrack.insertion.JsonConfig.linkedFrom.push({
					linkType: 'end',
					sourceLinkType: 'both',
					...(opts || {}),
					id: track.id,
				});
				destTrack.__emitDataChanged();
			}
		}

		const unlink = (sourceLinkType) => {
			track.insertion.JsonConfig.linkedTo = track.insertion.JsonConfig.linkedTo.map(linkedTo => {
				if (!sourceLinkType || linkedTo.sourceLinkType === sourceLinkType) {
					const destTrackId = linkedTo.id;
					const destTrack = track.__tracker.tracks.find(f => f.id === destTrackId);
					if (destTrack) {
						destTrack.insertion.JsonConfig.linkedFrom = destTrack.insertion.JsonConfig.linkedFrom.filter(f => f?.id !== track.id);
						destTrack.__emitDataChanged();
					}
					return null;
				}
				return linkedTo;
			}).filter(f => f);
			track.__emitDataChanged();
		}

		const processLinkedTracks = (deltaChanges, immediate) => {
			if (track.__isSubTrack || track.__preventLinksProcessing) return;

			if (__DEBUG__)
				console.debug(`processLinkedTracks ${Data.secondsToDisplayValue(track.start)} ${track.name()}`/*, deltaChanges*/);

			if (!changedTracks[track.id]) {
				changedTracks[track.id] = {
					start: 0,
					end: 0,
					changes: {},
					track
				};
			}

			changedTracks[track.id].changes[deltaChanges.changed] = (changedTracks[track.id].changes[deltaChanges.changed] || 0) + (deltaChanges.delta || 0);

			if (tracker?.isPublishing || immediate) {
				processImmediate();
				return Promise.resolve(true);
			} else {
				let promise = new Promise(res => {
					resolves.push(res);
					process();
				});

				return promise;
			}
		}

		Object.defineProperty(track, '__link', {
			get: () => link
		});

		Object.defineProperty(track, '__unlink', {
			get: () => unlink
		});

		Object.defineProperty(track, '__linkedTo', {
			get: () => track.insertion.JsonConfig.linkedTo.map(f => {
				var trackTo = track.__tracker.tracks.filter(t => t.id === f?.id)[0];
				return {
					linkType: f.linkType,
					sourceLinkType: f.sourceLinkType,
					track: trackTo
				}
			}).filter(f => f.track)
		});

		Object.defineProperty(track, '__linkedFrom', {
			get: () => track.insertion.JsonConfig.linkedFrom.map(f => {
				var trackFrom = track.__tracker.tracks.filter(t => t.id === f?.id)[0];
				return {
					linkType: f.linkType,
					sourceLinkType: f.sourceLinkType,
					track: trackFrom
				}
			}).filter(f => f.track)
		});

		Object.defineProperty(track, '__processLinkedTracks', {
			get: () => processLinkedTracks
		});

		const getActiveSubTrackDeltaWidth = () => {
			const isMuted = track.__isMuted || !track.__aggregatedTrack.muted;
			if (!isMuted) return 0;
			const activeSubTrack = track.subTracks.find(f => !f.__isMuted);
			if (!activeSubTrack) return 0;
			return activeSubTrack.getTrimmedDuration() - track.getTrimmedDuration() - (activeSubTrack?.insertion?.JsonConfig?.parentStartDelta || 0);
		}
		Object.defineProperty(track, '__activeSubTrackDeltaWidth', {
			get: () => getActiveSubTrackDeltaWidth()
		});
	}
};
